import React, { useState, useEffect, useMemo } from 'react';
import api from '../../api/auth';

const LEVELS = ['6ème', '5ème', '4ème', '3ème', 'Seconde', 'Première', 'Terminale'];

const Students = () => {
  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [step, setStep] = useState(1);
  const [editingStudent, setEditingStudent] = useState(null);
  const [toast, setToast] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterClass, setFilterClass] = useState('all');

  // Credentials modal
  const [showCredentialsModal, setShowCredentialsModal] = useState(false);
  const [createdCredentials, setCreatedCredentials] = useState(null);

  // Parent search
  const [parentMode, setParentMode] = useState('new');
  const [parentSearch, setParentSearch] = useState('');
  const [parentResults, setParentResults] = useState([]);
  const [searchingParent, setSearchingParent] = useState(false);
  const [selectedParent, setSelectedParent] = useState(null);

  const [formData, setFormData] = useState({
    lastName: '',
    firstName: '',
    sexe: '',
    birth_date: '',
    birth_place: '',
    ine: '',
    address: '',
    phone: '',
    email: '',
    academic_year: '2026-2027',
    matricule: '',
    enrollment_type: 'nouveau',
    level: '',
    class_id: '',
    enrollment_date: new Date().toISOString().split('T')[0],
    is_active: true,
    parent_name: '',
    parent_phone: '',
    parent_email: '',
    parent_address: '',
  });

  useEffect(() => {
    if (toast) {
      const t = setTimeout(() => setToast(null), 4000);
      return () => clearTimeout(t);
    }
  }, [toast]);

  const showToast = (message, type = 'success') => setToast({ message, type });

  useEffect(() => {
    fetchStudents();
    fetchClasses();
  }, []);

  const fetchStudents = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await api.get('/students', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStudents(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchClasses = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await api.get('/classes', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setClasses(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Erreur classes:', error);
    }
  };

  const searchParents = async () => {
    if (parentSearch.length < 2) {
      setParentResults([]);
      return;
    }

    setSearchingParent(true);
    try {
      const token = localStorage.getItem('token');
      const res = await api.get(`/parents/search?q=${encodeURIComponent(parentSearch)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setParentResults(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('Erreur recherche parent:', err);
    } finally {
      setSearchingParent(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      if (parentMode === 'existing' && parentSearch.length >= 2) {
        searchParents();
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [parentSearch, parentMode]);

  const filteredStudents = useMemo(() => {
    return students.filter((s) => {
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        const name = (s.user?.name || '').toLowerCase();
        const mat = (s.matricule || '').toLowerCase();
        if (!name.includes(term) && !mat.includes(term)) return false;
      }
      if (filterClass !== 'all' && String(s.class_id) !== String(filterClass)) {
        return false;
      }
      return true;
    });
  }, [students, searchTerm, filterClass]);

  const classesByLevel = useMemo(() => {
    if (!formData.level) return classes;
    return classes.filter((c) => c.level === formData.level);
  }, [classes, formData.level]);

  const handleSubmit = async () => {
    try {
      const token = localStorage.getItem('token');
      const fullName = `${formData.lastName} ${formData.firstName}`.trim();

      const payload = {
        name: fullName,
        email: formData.email || null,
        matricule: formData.matricule || null,
        class_id: formData.class_id,
        sexe: formData.sexe,
        birth_date: formData.birth_date,
        birth_place: formData.birth_place,
        ine: formData.ine,
        address: formData.address,
        academic_year: formData.academic_year,
        parent_mode: parentMode,
        parent_name: formData.parent_name || null,
        parent_email: formData.parent_email || null,
        parent_phone: formData.parent_phone || null,
        parent_address: formData.parent_address || null,
        parent_guardian_id: selectedParent?.id || null,
      };

      if (editingStudent) {
        await api.put(`/students/${editingStudent.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        showToast('Élève modifié avec succès !', 'success');
        closeModal();
        fetchStudents();
      } else {
        const res = await api.post('/students', payload, {
          headers: { Authorization: `Bearer ${token}` },
        });

        setCreatedCredentials({
          student: {
            name: fullName,
            email: res.data?.generated_email || res.data?.data?.user?.email,
            password: res.data?.generated_password || 'password123',
            matricule: res.data?.data?.matricule,
            class_name: classes.find((c) => String(c.id) === String(formData.class_id))?.name,
          },
          parent: res.data?.parent || null,
        });
        setShowCredentialsModal(true);
        fetchStudents();
      }
    } catch (error) {
      console.error('Erreur:', error);
      const msg =
        error.response?.data?.error ||
        (typeof error.response?.data?.errors === 'object'
          ? Object.values(error.response.data.errors).flat().join(', ')
          : "Erreur lors de l'enregistrement");
      showToast(msg, 'error');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Voulez-vous supprimer cet élève ?')) return;
    try {
      const token = localStorage.getItem('token');
      await api.delete(`/students/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      showToast('Élève supprimé avec succès !', 'success');
      fetchStudents();
    } catch (error) {
      showToast('Erreur lors de la suppression', 'error');
    }
  };

  const handleEdit = (student) => {
    const fullName = student.user?.name || '';
    const parts = fullName.split(' ');
    const lastName = parts[0] || '';
    const firstName = parts.slice(1).join(' ') || '';

    setEditingStudent(student);
    setParentMode('none');
    setFormData({
      lastName,
      firstName,
      sexe: student.sexe || '',
      birth_date: student.birth_date_raw || student.birth_date || '',
      birth_place: student.birth_place || '',
      ine: student.ine || '',
      address: student.address || '',
      phone: student.user?.phone || '',
      email: student.user?.email || '',
      academic_year: student.academic_year || '2026-2027',
      matricule: student.matricule || '',
      enrollment_type: 'ancien',
      level: student.class?.level || '',
      class_id: student.class_id || '',
      enrollment_date: new Date().toISOString().split('T')[0],
      is_active: student.is_enrolled !== false,
      parent_name: student.parent_name || '',
      parent_phone: student.parent_phone || '',
      parent_email: student.parent_email || '',
      parent_address: '',
    });
    setStep(1);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingStudent(null);
    setStep(1);
    setParentMode('new');
    setParentSearch('');
    setParentResults([]);
    setSelectedParent(null);
    setFormData({
      lastName: '',
      firstName: '',
      sexe: '',
      birth_date: '',
      birth_place: '',
      ine: '',
      address: '',
      phone: '',
      email: '',
      academic_year: '2026-2027',
      matricule: '',
      enrollment_type: 'nouveau',
      level: '',
      class_id: '',
      enrollment_date: new Date().toISOString().split('T')[0],
      is_active: true,
      parent_name: '',
      parent_phone: '',
      parent_email: '',
      parent_address: '',
    });
  };

  const handleNew = () => {
    setEditingStudent(null);
    setStep(1);
    setShowModal(true);
  };

  const canGoNext = () => {
    if (step === 1) return formData.lastName && formData.firstName;
    if (step === 2) return formData.class_id && formData.academic_year;
    if (step === 3) {
      if (parentMode === 'new') return !!formData.parent_name;
      if (parentMode === 'existing') return !!selectedParent;
      return true;
    }
    return true;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return dateStr;
      const day = String(d.getDate()).padStart(2, '0');
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const year = d.getFullYear();
      return `${day}/${month}/${year}`;
    } catch {
      return dateStr;
    }
  };

  const handlePrint = () => {
    if (!createdCredentials) return;
    const s = createdCredentials.student;
    const p = createdCredentials.parent;

    const w = window.open('', '_blank');
    const html = `
      <html>
        <head>
          <title>Identifiants - ${s.name}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            .fiche { border: 2px solid #333; padding: 20px; margin-bottom: 20px; max-width: 500px; }
            .fiche h2 { margin: 0 0 10px 0; color: #E67E22; }
            .row { display: flex; margin: 6px 0; }
            .label { font-weight: bold; width: 140px; }
            .value { font-family: monospace; }
            .note { background: #FFF3CD; padding: 10px; margin-top: 15px; font-size: 12px; border-left: 4px solid #E67E22; }
            @media print { body { padding: 0; } .fiche { page-break-inside: avoid; } }
          </style>
        </head>
        <body>
          <div class="fiche">
            <h2>🎓 SamaEcole - Fiche Élève</h2>
            <div class="row"><span class="label">Nom :</span><span class="value">${s.name}</span></div>
            ${s.matricule ? `<div class="row"><span class="label">Matricule :</span><span class="value">${s.matricule}</span></div>` : ''}
            ${s.class_name ? `<div class="row"><span class="label">Classe :</span><span class="value">${s.class_name}</span></div>` : ''}
            <div class="row"><span class="label">📧 Email :</span><span class="value">${s.email}</span></div>
            <div class="row"><span class="label">🔑 Mot de passe :</span><span class="value">${s.password}</span></div>
            <div class="note">⚠️ Changez votre mot de passe à la 1ère connexion sur : http://samaecole.sn/login</div>
          </div>
          ${p && p.mode === 'new' ? `
          <div class="fiche">
            <h2>👨‍👩‍👧 SamaEcole - Fiche Parent</h2>
            <div class="row"><span class="label">Nom :</span><span class="value">${p.name}</span></div>
            <div class="row"><span class="label">📧 Email :</span><span class="value">${p.email}</span></div>
            <div class="row"><span class="label">🔑 Mot de passe :</span><span class="value">${p.password}</span></div>
            <div class="note">⚠️ Changez votre mot de passe à la 1ère connexion sur : http://samaecole.sn/login</div>
          </div>
          ` : ''}
          <script>window.print();</script>
        </body>
      </html>
    `;
    w.document.write(html);
    w.document.close();
  };

  const handleCopy = () => {
    if (!createdCredentials) return;
    const s = createdCredentials.student;
    const p = createdCredentials.parent;

    let text = `=== ÉLÈVE ===\nNom: ${s.name}\nEmail: ${s.email}\nMot de passe: ${s.password}`;
    if (p && p.mode === 'new') {
      text += `\n\n=== PARENT ===\nNom: ${p.name}\nEmail: ${p.email}\nMot de passe: ${p.password}`;
    }
    navigator.clipboard.writeText(text);
    showToast('Identifiants copiés !', 'success');
  };

  return (
    <div>
      {toast && (
        <div
          className={`fixed top-4 right-4 z-[100] ${
            toast.type === 'success' ? 'bg-green-500' : 'bg-red-500'
          } text-white px-6 py-4 rounded-lg shadow-2xl flex items-center gap-3 min-w-[280px]`}
        >
          <span className="text-xl">{toast.type === 'success' ? '✅' : '❌'}</span>
          <span className="flex-1 font-medium text-sm">{toast.message}</span>
          <button
            onClick={() => setToast(null)}
            className="text-white text-2xl font-bold leading-none"
          >
            ×
          </button>
        </div>
      )}

      <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#2C3E50]">👨‍🎓 Gestion des élèves</h1>
          <p className="text-gray-500">Ajoutez, modifiez ou supprimez des élèves</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={handleNew}
            className="bg-[#E67E22] text-white px-4 py-2 rounded-lg hover:bg-[#D35400] transition font-medium"
          >
            ➕ Créer un élève
          </button>
          <a
            href="/admin/students/import"
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition font-medium flex items-center gap-2"
          >
            📥 Importer Excel
          </a>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-md p-4 mb-4 grid grid-cols-1 md:grid-cols-3 gap-3">
        <input
          type="text"
          placeholder="🔍 Rechercher par nom ou matricule..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#E67E22]"
        />
        <select
          value={filterClass}
          onChange={(e) => setFilterClass(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#E67E22]"
        >
          <option value="all">Toutes les classes</option>
          {classes.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
        <div className="text-sm text-gray-500 flex items-center px-2">
          {filteredStudents.length} élève(s) affiché(s) sur {students.length}
        </div>
      </div>

      {loading ? (
        <div className="text-center py-10 text-gray-500">Chargement...</div>
      ) : (
        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Matricule</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Nom & Prénom</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Email</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Sexe</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Naissance</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Classe</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Parent</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredStudents.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="text-center py-8 text-gray-500">
                      {students.length === 0 ? 'Aucun élève' : 'Aucun résultat'}
                    </td>
                  </tr>
                ) : (
                  filteredStudents.map((s) => (
                    <tr key={s.id} className="border-t hover:bg-gray-50">
                      <td className="py-3 px-4 text-sm font-mono">{s.matricule || '-'}</td>
                      <td className="py-3 px-4 font-medium">{s.user?.name || '-'}</td>
                      <td className="py-3 px-4 text-sm">{s.user?.email || '-'}</td>
                      <td className="py-3 px-4 text-sm">
                        {s.sexe === 'M' ? '♂ M' : s.sexe === 'F' ? '♀ F' : '-'}
                      </td>
                      <td className="py-3 px-4 text-sm">
                        {s.birth_date || '-'}
                        {s.birth_place ? ` (${s.birth_place})` : ''}
                      </td>
                      <td className="py-3 px-4">
                        <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs">
                          {s.class?.name || '-'}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-sm">
                        {s.parent_name || '-'}
                        {s.parent_phone && (
                          <div className="text-xs text-gray-400">{s.parent_phone}</div>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <button
                          onClick={() => handleEdit(s)}
                          className="text-blue-600 hover:text-blue-800 mr-3"
                          title="Modifier"
                        >
                          ✏️
                        </button>
                        <button
                          onClick={() => handleDelete(s.id)}
                          className="text-red-600 hover:text-red-800"
                          title="Supprimer"
                        >
                          🗑️
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* MODAL WIZARD */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b">
              <h2 className="text-xl font-bold text-[#2C3E50] mb-3">
                {editingStudent ? '✏️ Modifier un élève' : '➕ Créer un élève'}
              </h2>

              <div className="flex items-center justify-between text-xs">
                {[
                  { n: 1, label: 'Infos personnelles', icon: '👤' },
                  { n: 2, label: 'Infos scolaires', icon: '🏫' },
                  { n: 3, label: 'Parent / Tuteur', icon: '👨‍👩‍👧' },
                  { n: 4, label: 'Confirmation', icon: '✅' },
                ].map((s, i) => (
                  <React.Fragment key={s.n}>
                    <div className="flex flex-col items-center flex-1">
                      <div
                        className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                          step >= s.n
                            ? 'bg-[#E67E22] text-white'
                            : 'bg-gray-200 text-gray-500'
                        }`}
                      >
                        {step > s.n ? '✓' : s.n}
                      </div>
                      <span
                        className={`mt-1 text-center ${
                          step >= s.n ? 'text-[#E67E22] font-medium' : 'text-gray-400'
                        }`}
                      >
                        {s.icon} {s.label}
                      </span>
                    </div>
                    {i < 3 && (
                      <div
                        className={`h-0.5 flex-1 ${
                          step > s.n ? 'bg-[#E67E22]' : 'bg-gray-200'
                        }`}
                      />
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>

            <div className="p-6 space-y-4">
              {/* ÉTAPE 1 - INFOS PERSO */}
              {step === 1 && (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Nom *
                      </label>
                      <input
                        type="text"
                        placeholder="Diop"
                        value={formData.lastName}
                        onChange={(e) =>
                          setFormData({ ...formData, lastName: e.target.value })
                        }
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#E67E22]"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Prénom *
                      </label>
                      <input
                        type="text"
                        placeholder="Aïssatou"
                        value={formData.firstName}
                        onChange={(e) =>
                          setFormData({ ...formData, firstName: e.target.value })
                        }
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#E67E22]"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Sexe
                      </label>
                      <select
                        value={formData.sexe}
                        onChange={(e) =>
                          setFormData({ ...formData, sexe: e.target.value })
                        }
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#E67E22]"
                      >
                        <option value="">-- Choisir --</option>
                        <option value="M">Masculin</option>
                        <option value="F">Féminin</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Date de naissance
                      </label>
                      <input
                        type="date"
                        value={formData.birth_date}
                        onChange={(e) =>
                          setFormData({ ...formData, birth_date: e.target.value })
                        }
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#E67E22]"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Lieu de naissance
                      </label>
                      <input
                        type="text"
                        placeholder="Dakar"
                        value={formData.birth_place}
                        onChange={(e) =>
                          setFormData({ ...formData, birth_place: e.target.value })
                        }
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#E67E22]"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        INE
                      </label>
                      <input
                        type="text"
                        placeholder="1234567890A"
                        value={formData.ine}
                        onChange={(e) =>
                          setFormData({ ...formData, ine: e.target.value })
                        }
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#E67E22]"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email
                      <span className="text-xs text-gray-400 ml-1">
                        (laisser vide pour auto-générer)
                      </span>
                    </label>
                    <input
                      type="email"
                      placeholder="eleve@email.com"
                      value={formData.email}
                      onChange={(e) =>
                        setFormData({ ...formData, email: e.target.value })
                      }
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#E67E22]"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Adresse
                    </label>
                    <input
                      type="text"
                      placeholder="Dakar, Sénégal"
                      value={formData.address}
                      onChange={(e) =>
                        setFormData({ ...formData, address: e.target.value })
                      }
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#E67E22]"
                    />
                  </div>
                </>
              )}

              {/* ÉTAPE 2 - INFOS SCOLAIRES */}
              {step === 2 && (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Année scolaire *
                      </label>
                      <select
                        value={formData.academic_year}
                        onChange={(e) =>
                          setFormData({ ...formData, academic_year: e.target.value })
                        }
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#E67E22]"
                      >
                        <option value="2025-2026">2025-2026</option>
                        <option value="2026-2027">2026-2027</option>
                        <option value="2027-2028">2027-2028</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Matricule
                        <span className="text-xs text-gray-400 ml-1">(auto si vide)</span>
                      </label>
                      <input
                        type="text"
                        placeholder="Généré automatiquement"
                        value={formData.matricule}
                        onChange={(e) =>
                          setFormData({ ...formData, matricule: e.target.value })
                        }
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 focus:outline-none focus:border-[#E67E22]"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Niveau *
                      </label>
                      <select
                        value={formData.level}
                        onChange={(e) =>
                          setFormData({ ...formData, level: e.target.value, class_id: '' })
                        }
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#E67E22]"
                      >
                        <option value="">-- Choisir un niveau --</option>
                        {LEVELS.map((lvl) => (
                          <option key={lvl} value={lvl}>{lvl}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Classe *
                      </label>
                      <select
                        value={formData.class_id}
                        onChange={(e) =>
                          setFormData({ ...formData, class_id: e.target.value })
                        }
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#E67E22]"
                        disabled={!formData.level}
                      >
                        <option value="">-- Choisir une classe --</option>
                        {classesByLevel.map((c) => (
                          <option key={c.id} value={c.id}>{c.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Statut
                    </label>
                    <div className="flex gap-4">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          checked={formData.is_active === true}
                          onChange={() => setFormData({ ...formData, is_active: true })}
                          className="accent-[#E67E22]"
                        />
                        <span className="text-sm">● Actif</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          checked={formData.is_active === false}
                          onChange={() => setFormData({ ...formData, is_active: false })}
                          className="accent-[#E67E22]"
                        />
                        <span className="text-sm">○ Inactif</span>
                      </label>
                    </div>
                  </div>
                </>
              )}

              {/* ÉTAPE 3 - PARENT */}
              {step === 3 && (
                <>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800 mb-2">
                    💡 Choisissez comment gérer le parent / tuteur
                  </div>

                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setParentMode('new')}
                      className={`flex-1 px-4 py-3 rounded-lg border-2 font-medium text-sm transition ${
                        parentMode === 'new'
                          ? 'border-[#E67E22] bg-orange-50 text-[#E67E22]'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      ➕ Créer un nouveau parent
                    </button>
                    <button
                      type="button"
                      onClick={() => setParentMode('existing')}
                      className={`flex-1 px-4 py-3 rounded-lg border-2 font-medium text-sm transition ${
                        parentMode === 'existing'
                          ? 'border-[#E67E22] bg-orange-50 text-[#E67E22]'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      🔍 Chercher un parent existant
                    </button>
                    <button
                      type="button"
                      onClick={() => setParentMode('none')}
                      className={`px-4 py-3 rounded-lg border-2 font-medium text-sm transition ${
                        parentMode === 'none'
                          ? 'border-[#E67E22] bg-orange-50 text-[#E67E22]'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      Aucun
                    </button>
                  </div>

                  {parentMode === 'new' && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Nom complet du parent *
                        </label>
                        <input
                          type="text"
                          placeholder="Mme Fatou Fall"
                          value={formData.parent_name}
                          onChange={(e) =>
                            setFormData({ ...formData, parent_name: e.target.value })
                          }
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#E67E22]"
                        />
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Téléphone
                          </label>
                          <input
                            type="text"
                            placeholder="77 123 45 67"
                            value={formData.parent_phone}
                            onChange={(e) =>
                              setFormData({ ...formData, parent_phone: e.target.value })
                            }
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#E67E22]"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Email
                            <span className="text-xs text-gray-400 ml-1">
                              (auto si vide)
                            </span>
                          </label>
                          <input
                            type="email"
                            placeholder="parent@email.com"
                            value={formData.parent_email}
                            onChange={(e) =>
                              setFormData({ ...formData, parent_email: e.target.value })
                            }
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#E67E22]"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Adresse
                        </label>
                        <input
                          type="text"
                          placeholder="Dakar, Sénégal"
                          value={formData.parent_address}
                          onChange={(e) =>
                            setFormData({ ...formData, parent_address: e.target.value })
                          }
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#E67E22]"
                        />
                      </div>

                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-xs text-yellow-800">
                        ⚠️ Un compte sera créé pour ce parent avec un mot de passe temporaire.
                      </div>
                    </>
                  )}

                  {parentMode === 'existing' && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          🔍 Rechercher par nom, email ou téléphone
                        </label>
                        <input
                          type="text"
                          placeholder="Ex: Fatou, 77..., fatou@..."
                          value={parentSearch}
                          onChange={(e) => setParentSearch(e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#E67E22]"
                        />
                      </div>

                      {searchingParent && (
                        <div className="text-sm text-gray-500">Recherche...</div>
                      )}

                      {parentResults.length > 0 && (
                        <div className="border rounded-lg max-h-60 overflow-y-auto">
                          {parentResults.map((p) => (
                            <button
                              key={p.id}
                              type="button"
                              onClick={() => setSelectedParent(p)}
                              className={`w-full text-left px-4 py-3 border-b last:border-0 hover:bg-gray-50 transition ${
                                selectedParent?.id === p.id ? 'bg-orange-50 border-l-4 border-l-[#E67E22]' : ''
                              }`}
                            >
                              <div className="font-medium text-sm">{p.name}</div>
                              <div className="text-xs text-gray-500">
                                📧 {p.email} {p.phone ? `• 📱 ${p.phone}` : ''}
                              </div>
                              {p.children_count > 0 && (
                                <div className="text-xs text-blue-600 mt-1">
                                  👧 {p.children_count} enfant(s) : {p.children.map((c) => c.name).join(', ')}
                                </div>
                              )}
                            </button>
                          ))}
                        </div>
                      )}

                      {parentSearch.length >= 2 && !searchingParent && parentResults.length === 0 && (
                        <div className="text-sm text-gray-500 text-center py-4">
                          Aucun parent trouvé
                        </div>
                      )}

                      {selectedParent && (
                        <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm">
                          ✅ Parent sélectionné : <strong>{selectedParent.name}</strong>
                          <button
                            type="button"
                            onClick={() => setSelectedParent(null)}
                            className="ml-2 text-red-500 text-xs underline"
                          >
                            Changer
                          </button>
                        </div>
                      )}
                    </>
                  )}

                  {parentMode === 'none' && (
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-sm text-gray-600 text-center">
                      Aucun parent ne sera associé à cet élève.
                    </div>
                  )}
                </>
              )}

              {/* ÉTAPE 4 - CONFIRMATION */}
              {step === 4 && (
                <>
                  <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                    <h3 className="font-bold text-[#2C3E50] mb-2">👤 Informations personnelles</h3>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <span className="text-gray-500">Nom :</span>
                      <span className="font-medium">{formData.lastName || '-'}</span>
                      <span className="text-gray-500">Prénom :</span>
                      <span className="font-medium">{formData.firstName || '-'}</span>
                      <span className="text-gray-500">Sexe :</span>
                      <span className="font-medium">
                        {formData.sexe === 'M' ? 'Masculin' : formData.sexe === 'F' ? 'Féminin' : '-'}
                      </span>
                      <span className="text-gray-500">Naissance :</span>
                      <span className="font-medium">
                        {formatDate(formData.birth_date)}
                        {formData.birth_place ? ` à ${formData.birth_place}` : ''}
                      </span>
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                    <h3 className="font-bold text-[#2C3E50] mb-2">🏫 Informations scolaires</h3>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <span className="text-gray-500">Année :</span>
                      <span className="font-medium">{formData.academic_year}</span>
                      <span className="text-gray-500">Niveau :</span>
                      <span className="font-medium">{formData.level || '-'}</span>
                      <span className="text-gray-500">Classe :</span>
                      <span className="font-medium">
                        {classes.find((c) => String(c.id) === String(formData.class_id))?.name || '-'}
                      </span>
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                    <h3 className="font-bold text-[#2C3E50] mb-2">👨‍👩‍👧 Parent / Tuteur</h3>
                    {parentMode === 'new' && (
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <span className="text-gray-500">Nom :</span>
                        <span className="font-medium">{formData.parent_name || '-'}</span>
                        <span className="text-gray-500">Téléphone :</span>
                        <span className="font-medium">{formData.parent_phone || '-'}</span>
                        <span className="text-gray-500">Email :</span>
                        <span className="font-medium">{formData.parent_email || '(auto-généré)'}</span>
                      </div>
                    )}
                    {parentMode === 'existing' && selectedParent && (
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <span className="text-gray-500">Nom :</span>
                        <span className="font-medium">{selectedParent.name}</span>
                        <span className="text-gray-500">Email :</span>
                        <span className="font-medium">{selectedParent.email || '-'}</span>
                        <span className="text-gray-500">Enfants déjà liés :</span>
                        <span className="font-medium">{selectedParent.children_count}</span>
                      </div>
                    )}
                    {parentMode === 'none' && (
                      <div className="text-sm text-gray-500 text-center py-2">
                        Aucun parent associé
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>

            <div className="px-6 py-4 border-t flex justify-between items-center">
              <button
                onClick={closeModal}
                className="text-gray-500 hover:text-gray-700 text-sm"
              >
                Annuler
              </button>

              <div className="flex gap-2">
                {step > 1 && (
                  <button
                    onClick={() => setStep(step - 1)}
                    className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm"
                  >
                    ← Retour
                  </button>
                )}

                {step < 4 ? (
                  <button
                    onClick={() => setStep(step + 1)}
                    disabled={!canGoNext()}
                    className="px-4 py-2 bg-[#E67E22] text-white rounded-lg hover:bg-[#D35400] disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                  >
                    Suivant →
                  </button>
                ) : (
                  <button
                    onClick={handleSubmit}
                    className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium"
                  >
                    ✅ {editingStudent ? 'Enregistrer' : "Créer l'élève"}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* MODAL IDENTIFIANTS */}
      {showCredentialsModal && createdCredentials && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b bg-green-50 rounded-t-2xl">
              <h2 className="text-xl font-bold text-green-700">
                ✅ Élève créé avec succès !
              </h2>
            </div>

            <div className="p-6 space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm font-semibold text-blue-900 mb-3">
                  🎓 IDENTIFIANTS ÉLÈVE
                </p>
                <div className="space-y-2 text-sm">
                  <div>
                    <span className="text-gray-500">Nom :</span>
                    <span className="font-medium ml-2">{createdCredentials.student.name}</span>
                  </div>
                  {createdCredentials.student.class_name && (
                    <div>
                      <span className="text-gray-500">Classe :</span>
                      <span className="font-medium ml-2">{createdCredentials.student.class_name}</span>
                    </div>
                  )}
                  {createdCredentials.student.matricule && (
                    <div>
                      <span className="text-gray-500">Matricule :</span>
                      <span className="font-mono font-medium ml-2">{createdCredentials.student.matricule}</span>
                    </div>
                  )}
                  <div>
                    <span className="text-gray-500">📧 Email :</span>
                    <div className="font-mono text-xs bg-white px-2 py-1 rounded border mt-1 break-all">
                      {createdCredentials.student.email}
                    </div>
                  </div>
                  <div>
                    <span className="text-gray-500">🔑 Mot de passe :</span>
                    <div className="font-mono text-sm bg-white px-2 py-1 rounded border mt-1">
                      {createdCredentials.student.password}
                    </div>
                  </div>
                </div>
              </div>

              {createdCredentials.parent && createdCredentials.parent.mode === 'new' && (
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                  <p className="text-sm font-semibold text-orange-900 mb-3">
                    👨‍👩‍👧 IDENTIFIANTS PARENT
                  </p>
                  <div className="space-y-2 text-sm">
                    <div>
                      <span className="text-gray-500">Nom :</span>
                      <span className="font-medium ml-2">{createdCredentials.parent.name}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">📧 Email :</span>
                      <div className="font-mono text-xs bg-white px-2 py-1 rounded border mt-1 break-all">
                        {createdCredentials.parent.email}
                      </div>
                    </div>
                    <div>
                      <span className="text-gray-500">🔑 Mot de passe :</span>
                      <div className="font-mono text-sm bg-white px-2 py-1 rounded border mt-1">
                        {createdCredentials.parent.password}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {createdCredentials.parent && createdCredentials.parent.mode === 'existing' && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-sm">
                  ✅ Élève lié au parent existant : <strong>{createdCredentials.parent.name}</strong>
                </div>
              )}

              <div className="bg-yellow-50 border border-yellow-300 text-yellow-800 px-3 py-2 rounded-lg text-xs">
                ⚠️ Les utilisateurs devront changer leur mot de passe à leur 1ère connexion.
              </div>

              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={handleCopy}
                  className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium text-sm"
                >
                  📋 Copier
                </button>
                <button
                  type="button"
                  onClick={handlePrint}
                  className="flex-1 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium text-sm"
                >
                  🖨️ Imprimer
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowCredentialsModal(false);
                    setCreatedCredentials(null);
                    closeModal();
                  }}
                  className="flex-1 px-4 py-2 bg-[#E67E22] hover:bg-[#D35400] text-white rounded-lg font-medium text-sm"
                >
                  ✖ Fermer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Students;